import { useEffect, useState } from "react";
import { Box, CircularProgress, IconButton, TextField } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { keccak256 } from "ethers";

import useRelayAction from "../../hooks/useRelayAction";
import { useWetInkFlow } from "../DigitalSignature/useWetInkFlow";
import { WetInkUploadOutcome } from "../DigitalSignature/useWetInkSigner";
import { validateScan } from "../DigitalSignature/wetInkScan";
import { DocumentType } from "../DigitalSignature/ipfsDocumentTypes";
import {
  ecoFuturesBankContractAddress,
  getReadOnlyContract,
  getReadOnlyProvider,
} from "../../services/contract.service";
import { PopupActionButton, PopupGhostButton, PopupProcessingButton } from "../PopupButtons";
import PopupDialog from "../PopupDialog";
import ProgressStepper from "../ProgressStepper";
import DeedDropzone from "./DeedDropzone";
import {
  CalloutText,
  DialogSheet,
  ErrorNote,
  Footnote,
  HashLine,
  InfoCallout,
  figmaButtonSx,
} from "./settlementStyles";

// Bound-verifier dialog for EcoBank.confirmActivation(tokenId, deedHash, deedDate) — the
// verifier's only settlement duty. Gasless lane: one EIP-712 signature via the backend
// relay, resolved only once DeedAnchored is observed on-chain.
//
// The deed itself is produced here rather than assumed to exist: the trust downloads the
// generated Deed of Assignment, hand-signs it, and uploads the scan, which the backend
// runs through wet-ink integrity verification (same lane as BecomeTrust / LandVerification).
// Only a scan that passes can be anchored, so the hash on-chain always corresponds to a
// document the system has checked against its own reference copy.
//
// deedHash = keccak256 of the uploaded scan's bytes. The exact derivation rule (raw bytes
// vs IPFS-CID-based) is still pending contract-team confirmation — this implements the
// "keccak of the deed document" fallback from the integration plan.
//
// Presentation follows the V9 Figma (nodes 1719:4284 / 1719:4285 / 1719:4390 / 1720:7135):
// the three stages that were previously one scrolling form are now a wizard, one per screen.

// TESTING OVERRIDE — lets a scan that the wet-ink check rejected be anchored anyway, so the
// settlement flow can be walked end-to-end without producing a genuinely hand-signed deed.
// Set to false before this reaches production.
//
// This is frontend-only, and it does NOT bypass the backend: POST /wet-ink-signature still
// 400s, so no signature is recorded and the IpfsDocument stays un-finalised. What gets anchored
// is keccak256 of the local file — confirmActivation takes only (tokenId, deedHash, deedDate)
// and never consults the document record, so the chain accepts it. The consequence is a deed
// hash on-chain that the system never verified against its reference copy, which is exactly
// what the check exists to prevent.
const ALLOW_UNVERIFIED_DEED_SCAN = true;

const STEPS = ["Download", "Upload", "Anchor"];

export interface AnchorDeedDialogProps {
  open: boolean;
  onClose: () => void;
  // On-chain token id (the Bank's settlement key).
  tokenId: number | string;
  // When KYC cleared; the contract requires deedDate >= kycConfirmedAt.
  kycConfirmedAt?: Date | null;
  // Pre-filled Deed of Assignment placeholders (<<DISTRICT>>, <<LAND_NAME>>, …). Anything
  // omitted stays an unfilled token in the generated PDF for the notary to complete.
  templateVars?: Record<string, string>;
  // Called after DeedAnchored is observed on-chain.
  onAnchored?: () => void;
}

// Today as the date input's YYYY-MM-DD, in the viewer's own calendar (what the picker shows).
const todayLocal = () => {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const STATUS_COPY: Record<string, string> = {
  signing: "Waiting for your wallet signature…",
  relaying: "Submitting to the relay…",
  confirming: "Waiting for on-chain confirmation…",
};

// The scan verdict, in the redesign's language. WetInkOutcome renders the same information but
// in the pre-redesign styling, so the mapping lives here rather than restyling a component four
// other flows still share.
const OutcomeCallout = ({ outcome }: { outcome: WetInkUploadOutcome }) => {
  if (outcome.kind === "success") {
    return (
      <InfoCallout surface="white">
        <CalloutText>
          <strong>Signature recorded.</strong> The scan passed the integrity check and can be anchored.
        </CalloutText>
      </InfoCallout>
    );
  }

  if (outcome.kind === "tampered") {
    return (
      <InfoCallout surface="white" tone="error">
        <CalloutText>
          <strong>Verification failed</strong>
          <br />
          <span style={{ color: "#888" }}>{outcome.summary || "Differences detected"}</span>
        </CalloutText>
        {outcome.discrepancies && outcome.discrepancies.length > 0 && (
          <Box
            component="ul"
            sx={{
              margin: "10px 0 0",
              paddingLeft: "18px",
              fontFamily: "'Inter', sans-serif",
              fontSize: "14px",
              lineHeight: "18px",
              color: "#000",
              "& li": { marginBottom: "8px" },
              "& li:last-of-type": { marginBottom: 0 },
            }}
          >
            {outcome.discrepancies.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </Box>
        )}
      </InfoCallout>
    );
  }

  return (
    <InfoCallout surface="white" tone={outcome.kind === "transient" ? "progress" : "error"}>
      <CalloutText>
        {outcome.kind === "transient" ? (
          <>
            <strong>Verification temporarily unavailable.</strong> {outcome.message} Nothing was rejected — retry the
            same upload shortly.
          </>
        ) : (
          outcome.message
        )}
      </CalloutText>
    </InfoCallout>
  );
};

const AnchorDeedDialog = ({
  open,
  onClose,
  tokenId,
  kycConfirmedAt,
  templateVars,
  onAnchored,
}: AnchorDeedDialogProps) => {
  const [step, setStep] = useState(0);
  const [deedHash, setDeedHash] = useState<string | null>(null);
  const [scan, setScan] = useState<File | null>(null);
  const [deedDate, setDeedDate] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const { run, reset, status, error, isPending } = useRelayAction();

  // Deed of Assignment — "assignment and assumption of ecological covenant on transfer of
  // land", i.e. the instrument that moves the covenant position to the auction winner. The
  // trust is its sole in-system signer; the paper copy still carries the grantors' and
  // notary's wet signatures.
  const {
    documentId,
    isCompleted,
    downloadLoading,
    downloadError,
    handleDownload,
    uploadLoading,
    outcome,
    handleUpload,
    clearOutcome,
  } = useWetInkFlow({ type: DocumentType.DEED_OF_ASSIGNMENT, templateVars });

  // Only a scan that cleared the integrity check may be anchored — unless the testing override
  // is on, in which case any readable file can be carried through, whatever the backend said
  // about it (or without asking the backend at all).
  //
  // This used to bypass only a "tampered" or "transient" verdict, which left the escape hatch
  // unreachable in exactly the cases it exists for. Every other way the upload fails parses as
  // a plain `error` — POST /ipfs-document 403 for a wallet without the trust role, "Document is
  // already finalized" / "This address has already signed" once an earlier run completed the
  // document, auth or network failures — and none of those unlocked anchoring. Worse, a
  // completed document disables "Upload & verify" outright (`isCompleted`), so no verdict could
  // be produced at all; and because the dialog stays mounted under SettlementPanel, that dead
  // document id survives closing and reopening it.
  //
  // So the override now keys off the local file alone: a hash is all confirmActivation needs.
  const bypassedVerdict = ALLOW_UNVERIFIED_DEED_SCAN && !!deedHash && outcome?.kind !== "success";

  const handleFile = async (file: File) => {
    setLocalError(null);
    // A new file invalidates any previous verdict — otherwise a verified verdict could be
    // carried over onto the hash of a different document.
    clearOutcome();
    setScan(null);
    setDeedHash(null);

    const invalid = validateScan(file);
    if (invalid) {
      setLocalError(invalid);
      return;
    }
    try {
      const bytes = new Uint8Array(await file.arrayBuffer());
      setDeedHash(keccak256(bytes));
      setScan(file);
    } catch {
      setLocalError("Could not read the deed file — please try again");
    }
  };

  // Downloading is what creates the document, so it is also what unlocks the upload step.
  // handleDownload resolves without returning the new id (it lands in the hook's own state), so
  // the advance is deferred to the render that has it rather than read back synchronously — and
  // it is gated on a flag so merely *having* a document id doesn't skip the step on reopen.
  const [advanceAfterDownload, setAdvanceAfterDownload] = useState(false);

  useEffect(() => {
    if (!advanceAfterDownload) {
      return;
    }
    setAdvanceAfterDownload(false);
    if (documentId != null && !downloadError) {
      setStep(1);
    }
  }, [advanceAfterDownload, documentId, downloadError]);

  const downloadThenAdvance = async () => {
    await handleDownload();
    setAdvanceAfterDownload(true);
  };

  const handleVerify = async () => {
    if (!scan) {
      return;
    }
    setLocalError(null);
    await handleUpload(scan);
  };

  const handleAnchor = async () => {
    if (!deedHash || !deedDate) {
      return;
    }
    // The deed date is a calendar day (UTC midnight). The contract requires
    // deedDate >= kycConfirmedAt, so a deed signed the same day KYC cleared would fail
    // the check at midnight — clamp up to kycConfirmedAt for same-day anchoring (this
    // is routine on the accelerated testnet where the whole lifecycle runs in minutes).
    const midnightUnix = Math.floor(new Date(`${deedDate}T00:00:00Z`).getTime() / 1000);
    const kycConfirmedUnix = kycConfirmedAt ? Math.floor(kycConfirmedAt.getTime() / 1000) : 0;
    if (kycConfirmedUnix && midnightUnix + 86400 <= kycConfirmedUnix) {
      setLocalError("The deed date cannot be before the day KYC was confirmed");
      return;
    }
    setLocalError(null);

    const bank = getReadOnlyContract("ecofutures-bank");
    if (!bank) {
      setLocalError("Bank contract not found");
      return;
    }
    // V10.1.2 also rejects a deed date in the FUTURE (DeadlinePassed). "Today" picked east of UTC
    // is UTC midnight of a day that has not started yet — in Sri Lanka, for 5½ hours after local
    // midnight — so cap it at the chain's own clock, which is what the contract compares against.
    let chainNow: number;
    try {
      const latest = await getReadOnlyProvider().getBlock("latest");
      if (!latest) {
        throw new Error("no block");
      }
      chainNow = Number(latest.timestamp);
    } catch {
      setLocalError("Could not read the chain's clock to date the deed — please try again");
      return;
    }
    const deedDateUnix = Math.min(Math.max(midnightUnix, kycConfirmedUnix), chainNow);
    const result = await run({
      to: ecoFuturesBankContractAddress,
      iface: bank.interface,
      fn: "confirmActivation",
      args: [tokenId, deedHash, deedDateUnix],
      confirmation: {
        address: ecoFuturesBankContractAddress,
        iface: bank.interface,
        eventName: "DeedAnchored",
      },
      label: "Anchor deed",
    });
    if (result) {
      onAnchored?.();
      handleClose();
    }
  };

  const handleClose = () => {
    if (isPending || uploadLoading) {
      return;
    }
    reset();
    clearOutcome();
    setStep(0);
    setDeedHash(null);
    setScan(null);
    setDeedDate("");
    setLocalError(null);
    onClose();
  };

  const busy = isPending || uploadLoading || downloadLoading;
  // The upload step turns red whenever the scan did not clear, override or not: skipping is now
  // a deliberate second click rather than the primary button quietly changing meaning, so a red
  // step reads as the honest record of what happened instead of as a bug.
  const errorStep = outcome && outcome.kind !== "success" ? 1 : undefined;

  return (
    <PopupDialog open={open} onClose={handleClose} surface="sheet">
      <DialogSheet>
        {/* Drawn on every step, including the first — where there is no previous step, so it
            leaves the dialog instead. */}
        <Box sx={{ display: "flex", alignItems: "center", minHeight: 30 }}>
          <IconButton
            aria-label={step > 0 ? "Back" : "Close"}
            onClick={() => (step > 0 ? setStep((s) => s - 1) : handleClose())}
            disabled={busy}
            sx={{ padding: 0, color: "#000" }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Box>

        {/* On the sheet the stepper sits directly on the grey, without its usual blue block. */}
        <ProgressStepper
          steps={STEPS}
          activeStep={step}
          errorStep={errorStep}
          sx={{ backgroundColor: "transparent", padding: 0 }}
        />

        {/* 1 · Generate + download the exact copy to print and sign. */}
        {step === 0 && (
          <>
            <InfoCallout surface="white">
              <CalloutText>
                Re-download before signing. The hash is stored on-chain, the file stays encrypted on your device.
                Approval needs wallet signature. No gas or BNB required.
              </CalloutText>
            </InfoCallout>
            {downloadError && <ErrorNote>{downloadError}</ErrorNote>}
            {downloadLoading ? (
              <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
                <CircularProgress size={20} sx={{ color: "white" }} />
              </PopupProcessingButton>
            ) : (
              <PopupActionButton
                onClick={downloadThenAdvance}
                disabled={busy}
                variant="contained"
                fullWidth
                startIcon={<DownloadOutlinedIcon />}
                sx={figmaButtonSx}
              >
                {documentId ? "Re-download for signing" : "Download for signing"}
              </PopupActionButton>
            )}
            {/* Normally the download is what creates the document and so unlocks the upload step.
                With the override on this is offered regardless, so a failed create (no trust
                role, an already-finalised document from an earlier run) still leaves a way
                through — nothing downstream needs the document, only the local file's hash. */}
            {(documentId != null || ALLOW_UNVERIFIED_DEED_SCAN) && (
              <PopupGhostButton onClick={() => setStep(1)} disabled={busy} fullWidth>
                Continue to upload
              </PopupGhostButton>
            )}
          </>
        )}

        {/* 2 · Upload the hand-signed scan for integrity verification. */}
        {step === 1 && (
          <>
            {outcome ? (
              <OutcomeCallout outcome={outcome} />
            ) : (
              <InfoCallout surface="white">
                <CalloutText>
                  Scan or photograph the hand-signed pages into a single file. The scan is checked against the reference
                  copy before it can be anchored.
                </CalloutText>
              </InfoCallout>
            )}

            <DeedDropzone file={scan} onFile={handleFile} disabled={busy} />

            {deedHash && <HashLine label="Deed hash" value={deedHash} />}
            {localError && <ErrorNote>{localError}</ErrorNote>}

            {uploadLoading ? (
              <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
                <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
                Verifying the signed scan…
              </PopupProcessingButton>
            ) : outcome?.kind === "success" ? (
              <PopupActionButton onClick={() => setStep(2)} variant="contained" fullWidth sx={figmaButtonSx}>
                Continue
              </PopupActionButton>
            ) : (
              <>
                <PopupActionButton
                  onClick={handleVerify}
                  disabled={!scan || !documentId || isCompleted}
                  variant="contained"
                  fullWidth
                  sx={figmaButtonSx}
                >
                  Upload &amp; verify
                </PopupActionButton>
                {/* The testing escape hatch, kept as a separate, plainly-labelled action rather
                    than folded into the primary button — verification stays the default path,
                    and skipping it is always an explicit choice. */}
                {bypassedVerdict && (
                  <PopupGhostButton onClick={() => setStep(2)} disabled={busy} fullWidth>
                    Continue without verifying (testing)
                  </PopupGhostButton>
                )}
              </>
            )}
          </>
        )}

        {/* 3 · Deed date, then anchor. */}
        {step === 2 && (
          <>
            <InfoCallout surface="white">
              <CalloutText>Only the deed&apos;s hash and date go on-chain.</CalloutText>
            </InfoCallout>

            <TextField
              type="date"
              label="Deed date"
              value={deedDate}
              onChange={(e) => setDeedDate(e.target.value)}
              disabled={busy}
              InputLabelProps={{ shrink: true }}
              // A deed cannot be dated in the future (V10.1.2 reverts DeadlinePassed); handleAnchor
              // additionally caps the timestamp at the chain's clock.
              inputProps={{ max: todayLocal() }}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "16px" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#121212" },
              }}
            />

            {/* Testing override is carrying this scan — say so loudly, so an unverified anchor is
                never mistaken for a verified one in a demo or a screenshot. */}
            {bypassedVerdict && (
              <InfoCallout surface="white" tone="warning">
                <CalloutText>
                  <strong>Testing override:</strong> this scan was not verified and no signature was recorded.
                  Anchoring it still writes its hash on-chain.
                </CalloutText>
              </InfoCallout>
            )}

            {(localError || error) && <ErrorNote>{localError || error}</ErrorNote>}

            {isPending ? (
              <PopupProcessingButton disabled variant="contained" fullWidth sx={figmaButtonSx}>
                <CircularProgress size={20} sx={{ color: "white", mr: "10px" }} />
                {STATUS_COPY[status] ?? "Processing…"}
              </PopupProcessingButton>
            ) : (
              <PopupActionButton
                onClick={handleAnchor}
                disabled={!deedHash || !deedDate}
                variant="contained"
                fullWidth
                sx={figmaButtonSx}
              >
                Anchor deed
              </PopupActionButton>
            )}

            <PopupGhostButton onClick={handleClose} disabled={busy} fullWidth>
              Cancel
            </PopupGhostButton>
          </>
        )}
        <Footnote>
          Anchoring is signed with your wallet and submitted gaslessly — only the deed&apos;s hash and date go on-chain.
        </Footnote>
      </DialogSheet>
    </PopupDialog>
  );
};

export default AnchorDeedDialog;
